'use client';

import React, { useState } from 'react';
import { X, ChevronLeft, ChevronRight, Users, CreditCard, CheckCircle, Shield } from 'lucide-react';
import { toast } from 'sonner';

interface Hotel {
  id: string;
  name: string;
  location: string;
  courts: number;
  pricePerHour: number;
  currency: string;
  surface: string;
  image: string;
  imageAlt: string;
  stayAndPlay: boolean;
}

interface Props {
  hotel: Hotel;
  onClose: () => void;
}

const timeSlots = [
  { id: 'slot-0800', time: '08:00', available: true },
  { id: 'slot-0900', time: '09:00', available: true },
  { id: 'slot-1000', time: '10:00', available: false },
  { id: 'slot-1100', time: '11:00', available: false },
  { id: 'slot-1200', time: '12:00', available: true },
  { id: 'slot-1300', time: '13:00', available: true },
  { id: 'slot-1400', time: '14:00', available: true },
  { id: 'slot-1500', time: '15:00', available: false },
  { id: 'slot-1600', time: '16:00', available: true },
  { id: 'slot-1700', time: '17:00', available: true },
  { id: 'slot-1800', time: '18:00', available: true },
  { id: 'slot-1900', time: '19:00', available: false },
  { id: 'slot-2000', time: '20:00', available: true },
  { id: 'slot-2100', time: '21:00', available: true },
];

const steps = ['Date & Time', 'Players', 'Payment', 'Confirmation'];

export default function BookingModal({ hotel, onClose }: Props) {
  const [step, setStep] = useState(0);
  const [selectedDate, setSelectedDate] = useState('2026-04-20');
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [duration, setDuration] = useState(1);
  const [players, setPlayers] = useState(2);
  const [courtNum, setCourtNum] = useState(1);
  const [stayAndPlay, setStayAndPlay] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cardNumber, setCardNumber] = useState('');
  const [cardName, setCardName] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvv, setCardCvv] = useState('');

  const totalPrice = hotel.pricePerHour * duration;
  const serviceFee = Math.round(totalPrice * 0.1);
  const grandTotal = totalPrice + serviceFee;

  const handleConfirm = async () => {
    setLoading(true);
    // Backend integration point: POST /api/bookings { hotelId, courtNum, date, slot, duration, players }
    await new Promise((r) => setTimeout(r, 1800));
    setLoading(false);
    setStep(3);
    toast.success('Court booked successfully! Confirmation sent to your email.');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-3xl shadow-modal w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-slide-up">
        {/* Header */}
        <div className="sticky top-0 bg-white/95 backdrop-blur-sm border-b border-border px-6 py-4 rounded-t-3xl z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {step > 0 && step < 3 && (
                <button
                  onClick={() => setStep(step - 1)}
                  className="p-2 rounded-xl hover:bg-muted transition-colors"
                >
                  <ChevronLeft size={18} />
                </button>
              )}
              <div>
                <h2 className="text-lg font-semibold text-foreground">Reserve a Court</h2>
                <p className="text-sm text-muted-foreground truncate max-w-xs">{hotel.name}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 rounded-xl hover:bg-muted transition-colors">
              <X size={18} className="text-muted-foreground" />
            </button>
          </div>

          {/* Step Progress */}
          <div className="flex items-center gap-2 mt-4">
            {steps.map((s, i) => (
              <React.Fragment key={`step-${s}`}>
                <div className="flex items-center gap-2">
                  <div
                    className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold transition-all duration-200 ${
                      i < step
                        ? 'bg-primary text-primary-foreground'
                        : i === step
                        ? 'bg-primary text-primary-foreground ring-4 ring-primary/20'
                        : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {i < step ? <CheckCircle size={14} /> : i + 1}
                  </div>
                  <span className={`text-xs font-medium hidden sm:block ${i === step ? 'text-foreground' : 'text-muted-foreground'}`}>
                    {s}
                  </span>
                </div>
                {i < steps.length - 1 && (
                  <div className={`flex-1 h-0.5 rounded-full transition-all duration-300 ${i < step ? 'bg-primary' : 'bg-muted'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="p-6">
          {/* Step 0: Date & Time */}
          {step === 0 && (
            <div className="space-y-5 animate-fade-in">
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">Select Date</label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  min="2026-04-19"
                  className="w-full px-4 py-3 rounded-xl border border-border focus:outline-none focus:ring-2 focus:ring-primary/30 text-sm font-medium"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">Select Court</label>
                <div className="flex gap-2">
                  {Array.from({ length: hotel.courts }).map((_, i) => (
                    <button
                      key={`court-btn-${i + 1}`}
                      onClick={() => setCourtNum(i + 1)}
                      className={`flex-1 py-3 rounded-xl border text-sm font-semibold transition-all duration-150 ${
                        courtNum === i + 1
                          ? 'bg-primary/10 border-primary text-primary' :'border-border text-muted-foreground hover:border-primary/40'
                      }`}
                    >
                      Court {i + 1}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">
                  Available Time Slots
                </label>
                <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
                  {timeSlots.map((slot) => (
                    <button
                      key={slot.id}
                      disabled={!slot.available}
                      onClick={() => setSelectedSlot(slot.id)}
                      className={`py-2.5 rounded-xl text-sm font-medium transition-all duration-150 ${
                        !slot.available
                          ? 'bg-muted/50 text-muted-foreground/40 cursor-not-allowed line-through'
                          : selectedSlot === slot.id
                          ? 'bg-primary text-primary-foreground shadow-sm'
                          : 'bg-muted text-foreground hover:bg-primary/10 hover:text-primary'
                      }`}
                    >
                      {slot.time}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-primary" /> Selected</span>
                  <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-muted" /> Available</span>
                  <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-muted/50" /> Booked</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">Duration</label>
                <div className="flex gap-2">
                  {[1, 1.5, 2].map((d) => (
                    <button
                      key={`dur-${d}`}
                      onClick={() => setDuration(d)}
                      className={`flex-1 py-3 rounded-xl border text-sm font-semibold transition-all duration-150 ${
                        duration === d
                          ? 'bg-primary/10 border-primary text-primary' :'border-border text-muted-foreground hover:border-primary/40'
                      }`}
                    >
                      {d}h
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 1: Players */}
          {step === 1 && (
            <div className="space-y-5 animate-fade-in">
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1">Number of Players</label>
                <p className="text-xs text-muted-foreground mb-3">Padel is played 2v2. You can book for 2, 3 or 4 players.</p>
                <div className="flex gap-2">
                  {[2, 3, 4].map((n) => (
                    <button
                      key={`player-btn-${n}`}
                      onClick={() => setPlayers(n)}
                      className={`flex-1 py-4 rounded-xl border text-sm font-semibold transition-all duration-150 flex flex-col items-center gap-1 ${
                        players === n
                          ? 'bg-primary/10 border-primary text-primary' :'border-border text-muted-foreground hover:border-primary/40'
                      }`}
                    >
                      <Users size={20} />
                      {n} Players
                    </button>
                  ))}
                </div>
              </div>

              {hotel.stayAndPlay && (
                <div className="p-4 rounded-xl bg-secondary/10 border border-secondary/30">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-foreground">Add Stay & Play Bundle</p>
                      <p className="text-xs text-muted-foreground mt-0.5">Include hotel room + breakfast + court access</p>
                    </div>
                    <button
                      onClick={() => setStayAndPlay(!stayAndPlay)}
                      className={`relative w-11 h-6 rounded-full transition-all duration-200 ${
                        stayAndPlay ? 'bg-primary' : 'bg-muted'
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-all duration-200 ${
                          stayAndPlay ? 'left-5.5 translate-x-0.5' : 'left-0.5'
                        }`}
                      />
                    </button>
                  </div>
                  {stayAndPlay && (
                    <p className="text-xs text-primary mt-2 font-medium">
                      ✓ Stay & Play bundle added — hotel team will contact you to confirm room availability
                    </p>
                  )}
                </div>
              )}

              {/* Booking Summary */}
              <div className="bg-muted/50 rounded-xl p-4 space-y-2">
                <p className="text-sm font-semibold text-foreground mb-3">Booking Summary</p>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{hotel.name} — Court {courtNum}</span>
                  <span className="font-medium font-mono tabular-nums">{hotel.pricePerHour} TND/hr</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Duration</span>
                  <span className="font-medium font-mono tabular-nums">{duration}h</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Players</span>
                  <span className="font-medium">{players}</span>
                </div>
                <div className="border-t border-border pt-2 mt-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Court fee</span>
                    <span className="font-mono tabular-nums">{totalPrice} TND</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Service fee (10%)</span>
                    <span className="font-mono tabular-nums">{serviceFee} TND</span>
                  </div>
                  <div className="flex justify-between text-base font-bold mt-2">
                    <span>Total</span>
                    <span className="text-primary font-mono tabular-nums">{grandTotal} TND</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Payment */}
          {step === 2 && (
            <div className="space-y-4 animate-fade-in">
              <div className="flex items-center gap-2 p-3 rounded-xl bg-primary/5 border border-primary/20">
                <Shield size={16} className="text-primary flex-shrink-0" />
                <p className="text-xs text-primary font-medium">Secured by 256-bit SSL encryption. Your card details are never stored.</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">Card Number</label>
                <input
                  type="text"
                  value={cardNumber}
                  onChange={(e) => setCardNumber(e.target.value)}
                  placeholder="1234 5678 9012 3456"
                  maxLength={19}
                  className="w-full px-4 py-3 rounded-xl border border-border focus:outline-none focus:ring-2 focus:ring-primary/30 text-sm font-mono"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">Cardholder Name</label>
                <input
                  type="text"
                  value={cardName}
                  onChange={(e) => setCardName(e.target.value)}
                  placeholder="Thomas Müller"
                  className="w-full px-4 py-3 rounded-xl border border-border focus:outline-none focus:ring-2 focus:ring-primary/30 text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">Expiry Date</label>
                  <input
                    type="text"
                    value={cardExpiry}
                    onChange={(e) => setCardExpiry(e.target.value)}
                    placeholder="MM/YY"
                    maxLength={5}
                    className="w-full px-4 py-3 rounded-xl border border-border focus:outline-none focus:ring-2 focus:ring-primary/30 text-sm font-mono"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">CVV</label>
                  <input
                    type="password"
                    value={cardCvv}
                    onChange={(e) => setCardCvv(e.target.value)}
                    placeholder="•••"
                    maxLength={4}
                    className="w-full px-4 py-3 rounded-xl border border-border focus:outline-none focus:ring-2 focus:ring-primary/30 text-sm font-mono"
                  />
                </div>
              </div>

              {/* Final Summary */}
              <div className="bg-primary/5 rounded-xl p-4 border border-primary/15">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm font-semibold text-foreground">Total to Pay</p>
                    <p className="text-xs text-muted-foreground">{hotel.name} · Court {courtNum} · {duration}h</p>
                  </div>
                  <span className="text-2xl font-bold text-primary font-mono tabular-nums">{grandTotal} TND</span>
                </div>
              </div>

              {/* Payment methods */}
              <div className="flex items-center gap-3">
                <span className="text-xs text-muted-foreground">Accepted:</span>
                {['Visa', 'Mastercard', 'PayPal', 'Stripe'].map((pm) => (
                  <span key={`pm-${pm}`} className="px-2 py-1 rounded-md border border-border text-xs font-medium text-muted-foreground bg-white">
                    {pm}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Confirmation */}
          {step === 3 && (
            <div className="text-center py-6 animate-fade-in">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={32} className="text-primary" />
              </div>
              <h3 className="text-xl font-bold text-foreground mb-2">Booking Confirmed!</h3>
              <p className="text-sm text-muted-foreground mb-6">
                Your court at <strong>{hotel.name}</strong> is reserved. A confirmation email has been sent with your booking details.
              </p>

              <div className="bg-muted/50 rounded-xl p-4 text-left space-y-2 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Booking Ref</span>
                  <span className="font-mono font-semibold text-foreground tabular-nums">TP-2026-{hotel.id.slice(-3).toUpperCase()}{courtNum}04</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Court</span>
                  <span className="font-medium">{hotel.name} — Court {courtNum}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Date</span>
                  <span className="font-medium font-mono tabular-nums">{selectedDate}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Duration</span>
                  <span className="font-medium">{duration}h · {players} players</span>
                </div>
                <div className="flex justify-between text-sm font-bold">
                  <span>Total Paid</span>
                  <span className="text-primary font-mono tabular-nums">{grandTotal} TND</span>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  className="flex-1 py-3 rounded-xl border border-border text-sm font-semibold text-muted-foreground hover:bg-muted transition-colors"
                >
                  Done
                </button>
                <button
                  onClick={onClose}
                  className="flex-1 py-3 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors"
                >
                  View My Bookings
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer CTA */}
        {step < 3 && (
          <div className="sticky bottom-0 bg-white/95 backdrop-blur-sm border-t border-border px-6 py-4 rounded-b-3xl">
            <button
              onClick={step === 2 ? handleConfirm : () => setStep(step + 1)}
              disabled={step === 0 && !selectedSlot || loading}
              className={`w-full py-3.5 rounded-xl text-sm font-semibold transition-all duration-150 flex items-center justify-center gap-2 ${
                (step === 0 && !selectedSlot) || loading
                  ? 'bg-muted text-muted-foreground cursor-not-allowed'
                  : 'bg-primary text-primary-foreground hover:bg-primary/90 active:scale-[0.99] shadow-sm'
              }`}
            >
              {loading ? (
                <>
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                  Processing Payment...
                </>
              ) : step === 2 ? (
                <>
                  <CreditCard size={16} />
                  Confirm & Pay {grandTotal} TND
                </>
              ) : (
                <>
                  Continue
                  <ChevronRight size={16} />
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}